'use client';
import { jsx } from 'react/jsx-runtime';
import { Portal } from '@radix-ui/react-portal';
import { useEffect, useState, useCallback, forwardRef, useImperativeHandle } from 'react';
import { useHydrated } from './hooks.mjs';
import { S as Slot } from './Accordion-client-DnxrUKra.mjs';
import { D as DialogContext, u as useDialogContext, a as useDialogControl } from './context-client-C2amdg5z.mjs';

/**
 * @param lock - if true, prevents all scrolling by hijacking the scroll events
 */ const useScrollLock = (lock = false)=>{
    useEffect(()=>{
        if (!lock) {
            document.body.classList.remove("scroll-locked");
            return;
        }
        document.body.classList.add("scroll-locked");
        return ()=>{
            document.body.classList.remove("scroll-locked");
        };
    }, [
        lock
    ]);
};

/**
 * Source: https://dialog5.vercel.app/
 */ 
const cn = (...classes)=>classes.filter(Boolean).join(" ");
const DialogRoot = ({ children, open: externalOpen, onOpenChange, mode = "modal" })=>{
    const [open, setOpen] = useState(false);
    const setOpenState = useCallback((newOpen)=>{
        onOpenChange?.(newOpen);
        setOpen(newOpen);
    }, [
        onOpenChange
    ]);
    const preferredOpen = typeof externalOpen === "boolean" ? externalOpen : open;
    return /*#__PURE__*/ jsx(DialogContext.Provider, {
        value: {
            open: preferredOpen,
            setOpen: setOpenState,
            mode
        },
        children: children
    });
};
const DialogViewportInner = /*#__PURE__*/ forwardRef(({ children, className, lock = true, mode: overrideMode, ...props }, extRef)=>{
    const { open, setOpen, mode } = useDialogContext();
    const { ref } = useDialogControl({
        open,
        setOpen,
        mode: overrideMode || mode
    });
    useImperativeHandle(extRef, ()=>ref.current);
    useScrollLock(lock && open);
    /* The only case where any is allowed - @types/react being invalid */ const ariaProps = {
        inert: !open ? true : undefined,
        "aria-hidden": !open
    };
    return /*#__PURE__*/ jsx("dialog", {
        ...props,
        ...ariaProps,
        className: cn(className, "fr-dialog"),
        ref: ref,
        children: children
    });
});
const DialogViewport = ({ children, ...props })=>{
    const hasHydrated = useHydrated();
    if (!hasHydrated) return null;
    return /*#__PURE__*/ jsx(Portal, {
        children: /*#__PURE__*/ jsx(DialogViewportInner, {
            ...props,
            children: children
        })
    });
};
DialogViewport.displayName = "DialogViewport";
const DialogTrigger = ({ children, asChild, ...props })=>{
    const { setOpen } = useDialogContext();
    const Component = asChild ? Slot : "button";
    return /*#__PURE__*/ jsx(Component, {
        ...props,
        onClick: ()=>setOpen(true),
        children: children
    });
};
const DialogClose = ({ children, asChild, ...props })=>{
    const { setOpen } = useDialogContext();
    const Component = asChild ? Slot : "button";
    return /*#__PURE__*/ jsx(Component, {
        ...props,
        onClick: ()=>setOpen(false),
        children: children
    });
};
const DialogContent = /*#__PURE__*/ forwardRef(({ children, className, ...props }, extRef)=>{
    return /*#__PURE__*/ jsx("div", {
        ...props,
        className: cn(className, "fr-dialog__content"),
        ref: extRef,
        children: children
    });
});
DialogContent.displayName = "DialogContent";

export { DialogRoot as D, DialogViewport as a, DialogTrigger as b, DialogClose as c, DialogContent as d };
